import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Briefcase,
  Info,
  Plus,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import {
  prospectOpportunities as opportunitiesApi,
  prospects as prospectsApi,
  machineCatalog as machineCatalogApi,
} from "@/api";
import { formatApiError } from "@/lib/api";

function prospectDisplayName(
  prospect = {}
) {
  return (
    prospect.company ||
    [
      prospect.firstName ||
        prospect.first_name,
      prospect.lastName ||
        prospect.last_name,
    ]
      .filter(Boolean)
      .join(" ") ||
    prospect.name ||
    prospect.email ||
    "Contact"
  );
}

function normalizeProspect(
  prospect = {}
) {
  return {
    ...prospect,
    id:
      prospect.id ||
      prospect._id ||
      "",
    lifecycleStage:
      prospect.lifecycleStage ===
        "client" ||
      prospect.status === "client"
        ? "client"
        : "prospect",
  };
}

function emptyOpportunityItem() {
  return {
    catalog_item_id: "",
    section: "Produit Principal",
    description: "",
    reference: "",
    quantity: 1,
    unit: "Unité(s)",
    unit_price: "",
    vat_rate: "20",
    notes: "",
  };
}

function initialForm() {
  return {
    items: [emptyOpportunityItem()],
    estimated_amount: "",
    expected_purchase_date: "",
    next_action_at: "",
    comment: "",
  };
}

function opportunityTitle(
  form,
  prospect
) {
  return `${
    form.items?.[0]?.description?.trim() ||
    "Projet commercial"
  } - ${prospectDisplayName(
    prospect
  )}`;
}

export default function OpportunityCreateModal({
  open,
  prospects = [],
  lockedProspect = null,
  defaultProspect = null,
  onClose,
  onCreated,
}) {
  const [
    loadedProspects,
    setLoadedProspects,
  ] = useState([]);

  const [
    prospectsLoading,
    setProspectsLoading,
  ] = useState(false);

  const [
    prospectsError,
    setProspectsError,
  ] = useState("");

  const selectableProspects =
    useMemo(() => {
      const byId = new Map();

      [
        ...prospects,
        ...loadedProspects,
      ]
        .map(normalizeProspect)
        .filter(
          (prospect) =>
            prospect.id
        )
        .forEach((prospect) => {
          byId.set(
            prospect.id,
            prospect
          );
        });

      return Array.from(
        byId.values()
      );
    }, [
      loadedProspects,
      prospects,
    ]);

  const fixedProspect =
    lockedProspect ||
    defaultProspect ||
    null;

  const [
    prospectId,
    setProspectId,
  ] = useState(
    fixedProspect?.id || ""
  );

  const [form, setForm] =
    useState(initialForm);

  const [saving, setSaving] =
    useState(false);

  const [
    formError,
    setFormError,
  ] = useState("");


  const proposalTotals = useMemo(() => {
    return (form.items || []).reduce(
      (totals, item) => {
        const quantity = Number(item.quantity || 0);
        const unitPrice = Number(item.unit_price || 0);
        const vatRate = Number(item.vat_rate || 0);
        const lineHt = quantity * unitPrice;

        return {
          ht: totals.ht + lineHt,
          vat: totals.vat + lineHt * vatRate / 100,
        };
      },
      { ht: 0, vat: 0 }
    );
  }, [form.items]);

  useEffect(() => {
    if (!open) return;

    setProspectId(
      fixedProspect?.id || ""
    );
    setForm(initialForm());
    setFormError("");
  }, [
    open,
    fixedProspect?.id,
  ]);

  useEffect(() => {
    if (
      !open ||
      fixedProspect?.id
    ) {
      return undefined;
    }

    let cancelled = false;

    const loadProspects =
      async () => {
        setProspectsLoading(true);
        setProspectsError("");

        try {
          const response =
            await prospectsApi.listProspects({
              lifecycle_stage:
                "all",
            });

          const items =
            Array.isArray(response)
              ? response
              : response?.items ||
                response?.prospects ||
                response?.data ||
                [];

          if (!cancelled) {
            setLoadedProspects(
              Array.isArray(items)
                ? items
                : []
            );
          }
        } catch {
          if (!cancelled) {
            setProspectsError(
              "La liste des prospects et clients n’a pas pu être chargée."
            );
          }
        } finally {
          if (!cancelled) {
            setProspectsLoading(
              false
            );
          }
        }
      };

    loadProspects();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    fixedProspect?.id,
  ]);

  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState("");

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let cancelled = false;

    setCatalogLoading(true);
    setCatalogError("");

    machineCatalogApi
      .list()
      .then((items) => {
        if (!cancelled) {
          setCatalogItems(
            Array.isArray(items)
              ? items.filter((item) => item.active !== false)
              : []
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCatalogError(
            "Le catalogue n’a pas pu être chargé."
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCatalogLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (
      event
    ) => {
      if (
        event.key === "Escape" &&
        !saving
      ) {
        onClose?.();
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () =>
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
  }, [
    open,
    onClose,
    saving,
  ]);

  if (!open) {
    return null;
  }

  const selectedProspect =
    fixedProspect ||
    selectableProspects.find(
      (prospect) =>
        prospect.id === prospectId
    ) ||
    null;

  const updateForm = (
    field,
    value
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const submit = async (
    event
  ) => {
    event.preventDefault();

    const selectedId =
      fixedProspect?.id ||
      prospectId;

    const validItems = (form.items || []).filter((item) => String(item.description || "").trim());

    if (!selectedId) {
      setFormError(
        "Sélectionnez un prospect ou un client."
      );
      return;
    }

    if (!validItems.length) {
      setFormError("Ajoutez au moins un article ou une prestation.");
      return;
    }
    if (validItems.some((item) => !(Number(item.quantity) > 0))) {
      setFormError("Chaque quantité doit être supérieure à zéro.");
      return;
    }

    if (
      form.estimated_amount !==
      ""
    ) {
      const amount = Number(
        form.estimated_amount
      );

      if (
        !Number.isFinite(amount) ||
        amount < 0
      ) {
        setFormError(
          "Le montant estimé doit être valide."
        );
        return;
      }
    }

    setSaving(true);
    setFormError("");

    try {
      const payload = {
        title: opportunityTitle(form, selectedProspect || {}),
        product_name: validItems.map((item) => item.description).join(" + "),
        catalog_item_id: validItems.length === 1 ? validItems[0].catalog_item_id || null : null,
        items: validItems.map((item, index) => ({
          ...item,
          quantity: Number(item.quantity || 1),
          unit_price: Number(item.unit_price || 0),
          vat_rate: Number(item.vat_rate || 20),
          line_total:
            Number(item.quantity || 1) *
            Number(item.unit_price || 0),
          order: index,
          source: "human",
          semantic_tags: [
            "domain:commercial",
            "entity:opportunity_item",
            "source:human",
            item.catalog_item_id
              ? "item_type:catalog"
              : "item_type:free",
          ],
        })),
        estimated_amount:
          proposalTotals.ht,
        status: "new",
        expected_purchase_date:
          form.expected_purchase_date,
        next_action_at:
          form.next_action_at,
        comment:
          form.comment.trim(),
      };

      const created =
        await opportunitiesApi.create(
          selectedId,
          payload
        );

      toast.success(
        "Opportunité enregistrée."
      );

      onClose?.();

      try {
        await onCreated?.(
          created,
          selectedProspect
        );
      } catch {
        toast.error(
          "L’opportunité est créée, mais l’affichage n’a pas pu être actualisé."
        );
      }
    } catch (error) {
      setFormError(
        formatApiError(
          error.response?.data
            ?.detail
        ) ||
          "Création de l'opportunité impossible."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center acta-modal-overlay p-3 sm:p-4"
      onMouseDown={() => {
        if (!saving) {
          onClose?.();
        }
      }}
      data-testid="opportunity-create-modal"
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="opportunity-create-title"
        className="acta-modal-surface flex max-h-[calc(100dvh-24px)] w-full flex-col overflow-hidden rounded-[28px] border border-[var(--do-border)] bg-[var(--do-bg-solid)] shadow-2xl sm:max-h-[calc(100dvh-48px)] sm:max-w-4xl sm:rounded-[28px]"
        onMouseDown={(event) =>
          event.stopPropagation()
        }
        onSubmit={submit}
        data-testid="opportunity-create-form"
      >
        <header className="flex items-start justify-between gap-4 border-b border-[var(--do-border)] px-4 py-4 sm:px-6">
          <div>
            <h2
              id="opportunity-create-title"
              className="flex items-center gap-2 font-heading text-xl font-black text-[var(--do-ink)] sm:text-2xl"
            >
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--do-accent-soft)] text-[var(--do-accent)]">
                <Briefcase
                  size={20}
                  weight="bold"
                />
              </span>

              Créer une opportunité
            </h2>

            <p className="mt-2 text-sm text-[var(--do-muted)]">
              Créez la proposition commerciale vivante : articles, prix et prochaines actions ne seront saisis qu’une fois.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-muted)] transition hover:bg-[var(--do-bg-solid)] disabled:opacity-50"
            aria-label="Fermer"
          >
            <X
              size={19}
              weight="bold"
            />
          </button>
        </header>

        <div className="acta-modal-scroll min-h-0 flex-1">
        {formError ? (
          <div className="mx-4 mt-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-3 text-sm font-semibold text-[var(--do-accent)] sm:mx-6">
            {formError}
          </div>
        ) : null}

        <div className="grid gap-5 px-4 py-5 sm:px-6 lg:grid-cols-[minmax(0,1fr)_260px]">
          <div className="grid min-w-0 gap-4 sm:grid-cols-2">
            <label className="min-w-0 sm:col-span-2">
              <span className="text-xs font-bold text-[var(--do-muted)]">
                Prospect / Client *
              </span>

              {fixedProspect ? (
                <div className="mt-2 flex min-h-[46px] items-center rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-2)] px-4 text-sm font-bold text-[var(--do-ink)]">
                  {prospectDisplayName(
                    fixedProspect
                  )}
                </div>
              ) : (
                <select
                  value={prospectId}
                  onChange={(event) =>
                    setProspectId(
                      event.target.value
                    )
                  }
                  required
                  disabled={
                    prospectsLoading
                  }
                  className="mt-2 min-h-[46px] w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-sm text-[var(--do-ink)] outline-none transition focus:border-[var(--do-border-strong)] disabled:cursor-wait disabled:opacity-60"
                  data-testid="opportunity-prospect-select"
                >
                  <option value="">
                    {prospectsLoading
                      ? "Chargement des contacts…"
                      : "Choisir un prospect ou client"}
                  </option>

                  {selectableProspects.map(
                    (prospect) => (
                      <option
                        key={
                          prospect.id
                        }
                        value={
                          prospect.id
                        }
                      >
                        {prospectDisplayName(
                          prospect
                        )}{" "}
                        —{" "}
                        {prospect.lifecycleStage ===
                        "client"
                          ? "Client"
                          : "Prospect"}
                      </option>
                    )
                  )}
                </select>
              )}

              {prospectsError ? (
                <p
                  className="mt-2 text-xs font-semibold text-[var(--do-accent)]"
                  role="alert"
                >
                  {prospectsError}
                </p>
              ) : null}
            </label>

            <section className="min-w-0 sm:col-span-2" data-testid="opportunity-items-editor">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-bold text-[var(--do-muted)]">Articles et prestations *</span>
                <button type="button" onClick={() => updateForm("items", [...form.items, emptyOpportunityItem()])} className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-xs font-black" data-testid="opportunity-add-item"><Plus size={14}/>Ajouter</button>
              </div>
              <div className="mt-2 space-y-3">
                {form.items.map((line, index) => (
                  <div key={index} className="rounded-2xl border border-[var(--do-border)] p-3" data-testid={`opportunity-item-${index}`}>
                    <div className="flex items-center justify-between"><strong className="text-sm">Article {index + 1}</strong>{form.items.length > 1 && <button type="button" onClick={() => updateForm("items", form.items.filter((_, i) => i !== index))} className="text-xs font-black text-[var(--do-accent)]">Retirer</button>}</div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,1fr)_110px]">
                      <select value={line.catalog_item_id} onChange={(event) => { const item = catalogItems.find((candidate) => candidate.id === event.target.value); updateForm("items", form.items.map((current, i) => i === index ? {...current, catalog_item_id:event.target.value, description:item?.name || current.description, reference:item?.reference || ""} : current)); }} disabled={catalogLoading} className="min-h-[44px] rounded-xl border px-3 text-sm" data-testid="opportunity-item-catalog-select">
                        <option value="">Prestation libre</option>
                        {catalogItems.map((item) => <option key={item.id} value={item.id}>{item.name} — {item.reference || "Sans référence"}</option>)}
                      </select>
                      <input type="number" min="0.01" step="0.01" value={line.quantity} onChange={(event) => updateForm("items", form.items.map((current, i) => i === index ? {...current, quantity:event.target.value} : current))} className="min-h-[44px] rounded-xl border px-3 text-sm" aria-label="Quantité"/>
                    </div>
                    {!line.catalog_item_id && <input value={line.description} onChange={(event) => updateForm("items", form.items.map((current, i) => i === index ? {...current, description:event.target.value} : current))} placeholder="Description de la prestation" className="mt-3 min-h-[44px] w-full rounded-xl border px-3 text-sm"/>}
                    <div className="mt-3 grid gap-3 sm:grid-cols-3">
                      <label>
                        <span className="text-xs font-bold text-[var(--do-muted)]">Prix unitaire HT</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          inputMode="decimal"
                          value={line.unit_price}
                          onChange={(event) =>
                            updateForm(
                              "items",
                              form.items.map((current, i) =>
                                i === index
                                  ? { ...current, unit_price: event.target.value }
                                  : current
                              )
                            )
                          }
                          className="mt-1 min-h-[44px] w-full rounded-xl border px-3 text-sm"
                          data-testid="opportunity-item-unit-price"
                        />
                      </label>
                      <label>
                        <span className="text-xs font-bold text-[var(--do-muted)]">TVA %</span>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={line.vat_rate}
                          onChange={(event) =>
                            updateForm(
                              "items",
                              form.items.map((current, i) =>
                                i === index
                                  ? { ...current, vat_rate: event.target.value }
                                  : current
                              )
                            )
                          }
                          className="mt-1 min-h-[44px] w-full rounded-xl border px-3 text-sm"
                        />
                      </label>
                      <div className="rounded-xl bg-[var(--do-bg-solid)] px-3 py-2">
                        <div className="text-xs font-bold text-[var(--do-muted)]">Total HT</div>
                        <div className="mt-1 text-sm font-black">
                          {(
                            Number(line.quantity || 0) *
                            Number(line.unit_price || 0)
                          ).toFixed(2)} €
                        </div>
                      </div>
                    </div>
                    {line.reference && <p className="mt-2 text-xs text-[var(--do-muted)]">Réf. {line.reference}</p>}
                  </div>
                ))}
              </div>
              {catalogError ? <p className="mt-2 text-xs font-semibold text-[var(--do-accent)]">{catalogError}</p> : null}
              {!catalogLoading && !catalogError && catalogItems.length === 0 ? <p className="mt-2 text-xs text-[var(--do-muted)]">Le catalogue de ce tenant est vide. Les prestations libres restent disponibles.</p> : null}
            </section>

            <div className="min-w-0 rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] p-4">
              <div className="text-xs font-bold text-[var(--do-muted)]">
                Total de la proposition
              </div>
              <div className="mt-2 flex items-end justify-between gap-3">
                <div>
                  <div className="font-heading text-2xl font-black text-[var(--do-ink)]">
                    {proposalTotals.ht.toFixed(2)} € HT
                  </div>
                  <div className="mt-1 text-xs text-[var(--do-muted)]">
                    {(proposalTotals.ht + proposalTotals.vat).toFixed(2)} € TTC
                  </div>
                </div>
                <span className="rounded-full bg-[var(--do-accent-soft)] px-3 py-1 text-xs font-black text-[var(--do-accent)]">
                  Calcul automatique
                </span>
              </div>
            </div>

            <label className="min-w-0">
              <span className="text-xs font-bold text-[var(--do-muted)]">
                Date d’achat estimée
              </span>

              <input
                type="date"
                value={
                  form.expected_purchase_date
                }
                onChange={(event) =>
                  updateForm(
                    "expected_purchase_date",
                    event.target.value
                  )
                }
                className="mt-2 min-h-[46px] w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-sm text-[var(--do-ink)] outline-none transition focus:border-[var(--do-border-strong)]"
              />
            </label>

            <label className="min-w-0">
              <span className="text-xs font-bold text-[var(--do-muted)]">
                Date de relance
              </span>

              <input
                type="date"
                value={
                  form.next_action_at
                }
                onChange={(event) =>
                  updateForm(
                    "next_action_at",
                    event.target.value
                  )
                }
                className="mt-2 min-h-[46px] w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-sm text-[var(--do-ink)] outline-none transition focus:border-[var(--do-border-strong)]"
              />
            </label>

            <label className="min-w-0 sm:col-span-2">
              <span className="text-xs font-bold text-[var(--do-muted)]">
                Commentaire
              </span>

              <textarea
                rows={4}
                maxLength={500}
                value={form.comment}
                onChange={(event) =>
                  updateForm(
                    "comment",
                    event.target.value
                  )
                }
                placeholder="Informations complémentaires…"
                className="mt-2 w-full resize-y rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-sm text-[var(--do-ink)] outline-none transition focus:border-[var(--do-border-strong)]"
              />

              <div className="mt-1 text-right text-[11px] font-semibold text-[var(--do-muted)]">
                {form.comment.length}/500
              </div>
            </label>
          </div>

          <aside className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] p-4">
            <div className="flex items-center justify-between gap-3">
              <strong className="font-heading text-sm font-black text-[var(--do-ink)]">
                Statut
              </strong>

              <span className="rounded-full bg-[var(--do-accent-soft)] px-3 py-1 text-xs font-black text-[var(--do-accent)]">
                Nouvelle
              </span>
            </div>

            <p className="mt-2 text-sm leading-6 text-[var(--do-muted)]">
              La proposition sera créée dans le pipeline. Le devis officiel sera figé uniquement lors de la soumission à l’admin.
            </p>

            <div className="mt-4 border-t border-[var(--do-border)] pt-4">
              <div className="flex items-center gap-2 text-sm font-black text-[var(--do-ink)]">
                <Info
                  size={17}
                  weight="bold"
                />
                Informations
              </div>

              <p className="mt-2 text-sm leading-6 text-[var(--do-muted)]">
                Chaque modification sera historisée et taguée pour les statistiques et les agents IA.
              </p>
            </div>
          </aside>
        </div>
        </div>

        <footer className="acta-modal-footer flex flex-col-reverse gap-3 border-t border-[var(--do-border)] px-4 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:flex-row sm:justify-end sm:px-6">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="min-h-[46px] rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-5 py-3 text-sm font-black text-[var(--do-ink)] transition hover:bg-[var(--do-bg-solid)] disabled:opacity-50"
          >
            Annuler
          </button>

          <button
            type="submit"
            disabled={
              saving ||
              !(
                fixedProspect?.id ||
                prospectId
              ) ||
              !(form.items || []).some((item) => String(item.description || "").trim())
            }
            className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl bg-[var(--do-accent)] px-5 py-3 text-sm font-black text-[var(--do-on-accent)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="opportunity-create-submit"
          >
            <Plus
              size={17}
              weight="bold"
            />

            {saving
              ? "Création…"
              : "Créer la proposition"}
          </button>
        </footer>
      </form>
    </div>
  );
}
